import { defineMessages, useIntl } from 'react-intl-next';
import { Helmet } from 'react-helmet-async';
import { useRouter } from 'react-resource-router';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Button from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import Spinner from '@atlaskit/spinner';
import React, { FunctionComponent } from 'react';
import { useUpdateDetails } from './hooks/useUpdateDetails';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import {
  AdfContentSection,
  AppUpdatesChangelogSummary,
  AppImpactedSidebar,
  ContentImage,
} from './components';
import { Divider } from '@atlaskit/navigation-system';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';

const messages = defineMessages({
  backAction: {
    id: 'appUpdatesDetails.backAction',
    defaultMessage: 'Back to release notes',
    description: 'Button text to navigate back to the updates list',
  },
  notFound: {
    id: 'appUpdatesDetails.notFound',
    defaultMessage:
      'This release note could not be found. Use the back button to return to release notes.',
    description: 'Error message displayed when an update cannot be found',
  },
  error: {
    id: 'appUpdatesDetails.error',
    defaultMessage:
      'Unable to load this release note. Use the back button to return and try again.',
    description: 'Error message displayed when update fails to load',
  },
  loading: {
    id: 'appUpdatesDetails.loading',
    defaultMessage: 'Loading release note\u2026',
    description: 'Loading message while fetching update details',
  },
  keyChanges: {
    id: 'appUpdatesDetails.keyChanges',
    defaultMessage: 'Key changes',
    description: 'Section title for key changes',
  },
  benefits: {
    id: 'appUpdatesDetails.benefits',
    defaultMessage: 'Benefits',
    description: 'Section title for benefits',
  },
  reasonForChange: {
    id: 'appUpdatesDetails.reasonForChange',
    defaultMessage: 'Reason for the change',
    description: 'Section title for reason for change',
  },
  prepareForChange: {
    id: 'appUpdatesDetails.prepareForChange',
    defaultMessage: 'How to prepare for the change',
    description: 'Section title for prepare for change',
  },
  getStarted: {
    id: 'appUpdatesDetails.getStarted',
    defaultMessage: 'Getting started',
    description: 'Section title for getting started',
  },
  browserTabTitleSuffix: {
    id: 'appUpdatesDetails.browserTabTitleSuffix',
    defaultMessage: 'Release Notes | Atlassian Community',
    description: 'Browser tab title for the release note details page',
  },
});

const titleContainerStyles = xcss({
  marginBlockStart: 'space.300',
});

const mainSectionStyles = xcss({
  flex: 2,
});
const sideSectionStyles = xcss({
  flex: 1,
  marginBlockStart: 'space.500',
});

const imageContainerStyles = xcss({
  marginBlockEnd: 'space.400',
  overflow: 'hidden',
  lineHeight: 'space.0',
  maxWidth: '800px',
});

const contentSectionStyles = xcss({
  marginBlockStart: 'space.300',
});

const renderImage = (update: ReturnType<typeof useUpdateDetails>['update']) => {
  if (!update?.featuredImage) {
    return null;
  }

  const { url, altTag, title, width, height } = update.featuredImage;
  const isHorizontal = width >= height;

  return (
    <Box xcss={imageContainerStyles}>
      <ContentImage
        src={url}
        altTag={altTag}
        title={title}
        width={isHorizontal ? '100%' : width}
        height={isHorizontal ? 'auto' : height}
        aspectRatio={isHorizontal ? 'unset' : '4 / 3'}
      />
    </Box>
  );
};

export const AppUpdatesDetailsPage: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const [routerState] = useRouter();
  const { sendScreenEvent } = useAnalyticsEvent();

  const handleBackClick = () => window.history.back();

  const updateId = routerState.match.params.id || null;

  const { update, isUpdateLoading, hasErrors } = useUpdateDetails(updateId);

  // Fire screen event after data loads so we can include changelogId and updatesAppTypes attributes
  React.useEffect(() => {
    if (update && updateId) {
      const appTypes = update.productTypes?.join(',');
      sendScreenEvent(
        appUpdatesUIEvents.screen.appUpdatesDetailsScreen(updateId, appTypes)
          .data,
      );
    }
  }, [sendScreenEvent, update, updateId]);

  React.useEffect(() => {
    if (typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, left: 0 });
    }
  }, [updateId]);

  // UFOSegment provides interaction context for UFOLoadHold.
  // UFOLoadHold holds the page-load interaction open while data is loading;
  // react-ufo auto-completes and sends the event when isUpdateLoading becomes false.
  // It is rendered in all states (loading, error, success) so the hold is always released.
  return (
    <UFOSegment name="app-updates-details">
      <UFOLoadHold name="update-data" hold={isUpdateLoading} />

      {/* Loading state */}
      {isUpdateLoading && (
        <Box padding="space.300">
          <Stack space="space.300" alignInline="center">
            <Spinner label={formatMessage(messages.loading)} />
            <Text>{formatMessage(messages.loading)}</Text>
          </Stack>
        </Box>
      )}

      {/* Error or not found state */}
      {!isUpdateLoading && (!update || hasErrors) && (
        <Box as="main" padding="space.300">
          <Stack space="space.300">
            <Button
              appearance="primary"
              iconBefore={ArrowLeftIcon}
              onClick={handleBackClick}
            >
              {formatMessage(messages.backAction)}
            </Button>
            <Text color="color.text.danger">
              {formatMessage(hasErrors ? messages.error : messages.notFound)}
            </Text>
          </Stack>
        </Box>
      )}

      {/* Success state */}
      {!isUpdateLoading && update && !hasErrors && (
        <Box as="main" paddingBlockStart="space.300">
          <Helmet>
            <title>{formatMessage(messages.browserTabTitleSuffix)}</title>
          </Helmet>
          <Box>
            <Button
              appearance="subtle"
              iconBefore={ArrowLeftIcon}
              onClick={handleBackClick}
            >
              {formatMessage(messages.backAction)}
            </Button>
          </Box>
          <Flex gap="space.150">
            <Stack xcss={mainSectionStyles} space="space.400">
              {/* Title Section */}
              <Box xcss={titleContainerStyles}>
                <Heading as="h1" size="xxlarge">
                  {update.title}
                </Heading>
              </Box>

              {/* Changelog Summary */}
              {
                <AppUpdatesChangelogSummary
                  status={update.status}
                  type={update.type}
                  releaseStartDate={update.releaseStartDate}
                  releaseEndDate={update.releaseEndDate}
                />
              }
              <Divider />

              {/* Content Sections */}
              <Box xcss={contentSectionStyles}>
                {/* Summary */}
                {update.summary && (
                  <AdfContentSection content={update.summary} />
                )}

                {/* Featured Image */}
                {renderImage(update)}

                {/* Key Changes */}
                {update.keyChanges && (
                  <AdfContentSection
                    content={update.keyChanges}
                    title={formatMessage(messages.keyChanges)}
                  />
                )}

                {/* Benefits */}
                {update.benefitsList && (
                  <AdfContentSection
                    content={update.benefitsList}
                    title={formatMessage(messages.benefits)}
                  />
                )}

                {/* Reason for Change */}
                {update.reasonForChange && (
                  <AdfContentSection
                    content={update.reasonForChange}
                    title={formatMessage(messages.reasonForChange)}
                  />
                )}

                {/* Prepare for Change */}
                {update.prepareForChange && (
                  <AdfContentSection
                    content={update.prepareForChange}
                    title={formatMessage(messages.prepareForChange)}
                  />
                )}

                {/* Get Started */}
                {update.getStarted && (
                  <AdfContentSection
                    content={update.getStarted}
                    title={formatMessage(messages.getStarted)}
                  />
                )}
              </Box>
            </Stack>
            {/* Apps Impacted Sidebar */}
            {update.products && update.products.length > 0 && (
              <Box xcss={sideSectionStyles}>
                <AppImpactedSidebar products={update.productTypes} />
              </Box>
            )}
          </Flex>
        </Box>
      )}
    </UFOSegment>
  );
};

export default AppUpdatesDetailsPage;
